/**
 * Analytics Service
 *
 * API client for analytics and metrics endpoints.
 */
import api from './api';
import {
  AnalyticsDashboardData,
  AnalyticsQueryParams,
  AnalyticsSummary,
  ExecutionAnalyticsData,
  CostAnalyticsData,
  ExecutionTrend,
  CostTrend,
  AgentMetrics,
  AgentCostBreakdown,
  RecentExecution,
} from '../types/analytics';

export const analyticsService = {
  /**
   * Get analytics dashboard summary
   *
   * @param params - Query parameters including time range
   * @returns Dashboard summary data
   */
  async getDashboard(params?: AnalyticsQueryParams): Promise<AnalyticsDashboardData> {
    const response = await api.get<AnalyticsDashboardData>('/analytics/dashboard', {
      params,
    });
    return response.data;
  },

  /**
   * Get analytics summary only
   *
   * @param params - Query parameters
   * @returns Summary metrics
   */
  async getSummary(params?: AnalyticsQueryParams): Promise<AnalyticsSummary> {
    const response = await api.get<AnalyticsSummary>('/analytics/summary', {
      params,
    });
    return response.data;
  },

  /**
   * Get execution analytics
   *
   * @param params - Query parameters
   * @returns Execution analytics data
   */
  async getExecutionAnalytics(params?: AnalyticsQueryParams): Promise<ExecutionAnalyticsData> {
    const response = await api.get<ExecutionAnalyticsData>('/analytics/executions', {
      params,
    });
    return response.data;
  },

  /**
   * Get cost analytics
   *
   * @param params - Query parameters
   * @returns Cost analytics data
   */
  async getCostAnalytics(params?: AnalyticsQueryParams): Promise<CostAnalyticsData> {
    const response = await api.get<CostAnalyticsData>('/analytics/costs', {
      params,
    });
    return response.data;
  },

  /**
   * Get execution trend data
   *
   * @param params - Query parameters
   * @returns Execution trend data
   */
  async getExecutionTrend(params?: AnalyticsQueryParams): Promise<ExecutionTrend> {
    const response = await api.get<ExecutionTrend>('/analytics/executions/trend', {
      params,
    });
    return response.data;
  },

  /**
   * Get cost trend data
   *
   * @param params - Query parameters
   * @returns Cost trend data
   */
  async getCostTrend(params?: AnalyticsQueryParams): Promise<CostTrend> {
    const response = await api.get<CostTrend>('/analytics/costs/trend', {
      params,
    });
    return response.data;
  },

  /**
   * Get agent metrics
   *
   * @param params - Query parameters
   * @returns List of agent metrics
   */
  async getAgentMetrics(params?: AnalyticsQueryParams): Promise<AgentMetrics[]> {
    const response = await api.get<AgentMetrics[]>('/analytics/agents', {
      params,
    });
    return response.data;
  },

  /**
   * Get cost breakdown by agent
   *
   * @param params - Query parameters
   * @returns Cost breakdown by agent
   */
  async getCostByAgent(params?: AnalyticsQueryParams): Promise<AgentCostBreakdown[]> {
    const response = await api.get<AgentCostBreakdown[]>('/analytics/costs/by-agent', {
      params,
    });
    return response.data;
  },

  /**
   * Get recent executions
   *
   * @param limit - Number of executions to return
   * @returns List of recent executions
   */
  async getRecentExecutions(limit: number = 10): Promise<RecentExecution[]> {
    const response = await api.get<RecentExecution[]>('/analytics/executions/recent', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Export analytics data as CSV
   *
   * @param type - Type of data to export
   * @param params - Query parameters
   * @returns Blob with CSV data
   */
  async exportCsv(
    type: 'executions' | 'costs' | 'agents',
    params?: AnalyticsQueryParams
  ): Promise<Blob> {
    const response = await api.get(`/analytics/export/${type}`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export default analyticsService;
